// Review loop — the ONE staff-side plain-English label map for the three
// collection status vocabularies, so no staff surface renders a raw enum
// ("CHANGESREQUESTED", "PARTIALLYAPPROVED", "AWAITINGREVIEW"):
//   • item       — CollectionItemStatus   (what the request as a whole is doing)
//   • submission — StagedReviewStatus     (one submitted copy under review)
//   • package    — CollectionPackageStatus (the whole document request)
// Every staff badge, list STATUS cell and drawer header reads from here.
// The recipient portal keeps its own recipient-voice map
// (portal/lib/statusMap.ts) and never imports this file.

import type { BadgeProps } from "@/design-system/components/ui";
import type { CollectionItemStatus, CollectionPackageStatus } from "@/api/collectionPackages";
import type { StagedReviewMarker, StagedReviewStatus } from "@/api/collectionStaging";
import type {
  ApprovalChainRole,
  ApprovalChainStageStatus,
  ApprovalChainStatus,
} from "@/api/approvalChains";

export type StatusTone = NonNullable<BadgeProps["status"]>;

export interface StatusLabel {
  label: string;
  tone: StatusTone;
}

export const ITEM_STATUS: Record<CollectionItemStatus, StatusLabel> = {
  Pending: { label: "Not started", tone: "draft" },
  InProgress: { label: "In progress", tone: "info" },
  SubmittedForReview: { label: "Submitted for review", tone: "review" },
  ChangesRequested: { label: "Changes requested", tone: "error" },
  Approved: { label: "Approved", tone: "finalized" },
  Waived: { label: "Waived", tone: "archived" },
};

export const SUBMISSION_STATUS: Record<StagedReviewStatus, StatusLabel> = {
  AwaitingReview: { label: "Awaiting review", tone: "review" },
  Approved: { label: "Approved", tone: "finalized" },
  Returned: { label: "Returned", tone: "error" },
  Superseded: { label: "Replaced by a newer submission", tone: "archived" },
  // Never listed to staff (the server hides unconfirmed uploads); mapped so
  // the vocabulary stays exhaustive.
  Unconfirmed: { label: "Not yet submitted", tone: "draft" },
};

export const PACKAGE_STATUS: Record<CollectionPackageStatus, StatusLabel> = {
  Draft: { label: "Draft", tone: "draft" },
  Sent: { label: "Sent", tone: "info" },
  InProgress: { label: "In progress", tone: "info" },
  Submitted: { label: "Submitted", tone: "review" },
  PartiallyApproved: { label: "Partially approved", tone: "review" },
  Completed: { label: "Completed", tone: "finalized" },
  Cancelled: { label: "Cancelled", tone: "archived" },
  Archived: { label: "Archived", tone: "archived" },
};

export const itemStatusLabel = (s: CollectionItemStatus): string => ITEM_STATUS[s]?.label ?? "—";
export const submissionStatusLabel = (s: StagedReviewStatus): string =>
  SUBMISSION_STATUS[s]?.label ?? "—";
export const packageStatusLabel = (s: CollectionPackageStatus): string =>
  PACKAGE_STATUS[s]?.label ?? "—";

const MARKER_LABEL: Record<StagedReviewMarker, string> = {
  Resubmitted: "Resubmitted",
  Reopened: "Reopened",
};

/** The fields a review-list row (or the drawer) needs to compose its STATUS. */
export interface ReviewRowStatusInput {
  reviewStatus: StagedReviewStatus;
  itemStatus?: CollectionItemStatus | null;
  reviewMarker?: StagedReviewMarker | null;
  filedDocumentId?: string | null;
}

/**
 * The lifecycle STATUS of one submission row, as the review list and staging
 * queue show it (owner-approved L1–L6):
 *   L1 "Awaiting review"
 *   L2 "Returned — waiting on recipient"       (item Changes requested)
 *   L3 "Returned — recipient making changes"   (item In progress)
 *   L4 "Awaiting review · Resubmitted"
 *   L5 "Awaiting review · Reopened"
 *   L6 "Approved · Filed" / "Approved · Not filed"
 */
export function reviewRowStatus(row: ReviewRowStatusInput): StatusLabel {
  switch (row.reviewStatus) {
    case "AwaitingReview":
      return {
        label: row.reviewMarker
          ? `Awaiting review · ${MARKER_LABEL[row.reviewMarker]}`
          : "Awaiting review",
        tone: "review",
      };
    case "Returned":
      return {
        label:
          row.itemStatus === "InProgress"
            ? "Returned — recipient making changes"
            : "Returned — waiting on recipient",
        tone: "error",
      };
    case "Approved":
      return {
        label: row.filedDocumentId ? "Approved · Filed" : "Approved · Not filed",
        tone: "finalized",
      };
    default:
      return SUBMISSION_STATUS[row.reviewStatus] ?? { label: "—", tone: "draft" };
  }
}

/** "Package: Partially approved — 6 of 9 approved" (the drawer's secondary line). */
export function packageProgressLine(
  status: CollectionPackageStatus | null | undefined,
  approved: number | null | undefined,
  total: number | null | undefined,
): string | null {
  if (!status) return null;
  const base = `Package: ${packageStatusLabel(status)}`;
  return total && total > 0 ? `${base} — ${approved ?? 0} of ${total} approved` : base;
}

/**
 * Raw enum codes whose plain-English label differs from the code, across all
 * three vocabularies: every run-together code ("ChangesRequested") plus the
 * single-word codes that are not ordinary display words ("Superseded",
 * "Unconfirmed"). "Pending" is left out on purpose — it is also an ordinary
 * word on staff surfaces ("Pending recipient"). A staff surface that renders
 * one of these as text is printing a raw enum; the no-raw-enum guard test
 * asserts none appears (match case-insensitively — Badge uppercases with CSS,
 * so the DOM text keeps the code's casing but a reader sees capitals).
 */
export const RAW_CODES_THAT_MUST_NOT_RENDER: readonly string[] = Array.from(
  new Set(
    [
      ...Object.entries(ITEM_STATUS),
      ...Object.entries(SUBMISSION_STATUS),
      ...Object.entries(PACKAGE_STATUS),
    ]
      .filter(([code, v]) => code !== v.label)
      .map(([code]) => code)
      .filter((code) => /[a-z][A-Z]/.test(code) || code === "Superseded" || code === "Unconfirmed"),
  ),
);

// Approval chains — the chain and stage status WORDS come from the server
// (`statusLabel` on ApprovalChainDto / ApprovalChainStageDto); only the badge
// tone is decided here. The role labels mirror the server's
// ApprovalChainRules.RoleLabel for the editor's role picker, where no server
// label exists yet.
export const APPROVAL_CHAIN_STATUS_TONE: Record<ApprovalChainStatus, StatusTone> = {
  Draft: "draft",
  Active: "info",
  Returned: "error",
  Cancelled: "archived",
  Completed: "finalized",
};

export const APPROVAL_CHAIN_STAGE_TONE: Record<ApprovalChainStageStatus, StatusTone> = {
  Waiting: "draft",
  Active: "review",
  Completed: "finalized",
  Returned: "error",
  Void: "archived",
};

export const APPROVAL_CHAIN_ROLE_LABEL: Record<ApprovalChainRole, string> = {
  Broker: "Licensed Intermediary",
  Seller: "Note Holder",
  Buyer: "Borrower",
  BuyerSideSigner: "Co-Borrower / Guarantor",
  Phoenix: "Note Administrator",
};
